import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';
import SiteHeader, { HeaderButton } from '../../components/layout/SiteHeader';
import SiteFooter from '../../components/layout/SiteFooter';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import landingBackground from '../../assets/backgrounds/landingBackground.avif';
import { GOLDEN_MINOR } from '../../styles/golden';
import { BRAND, frostedSx } from '../../styles/ui';

const PasswordChange: React.FC = () => {
  const { changePassword, error, isLoading } = useAuth();
  const [changePasswordError, setChangePasswordError] = useState<string | null>(null);
  const [changePasswordSuccess, setChangePasswordSuccess] = useState<boolean>(false);

  // Form validation schema
  const validationSchema = Yup.object({
    currentPassword: Yup.string()
      .required('Current password is required'),
    newPassword: Yup.string()
      .min(8, 'Password must be at least 8 characters')
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
        'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
      )
      .required('New password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword')], 'Passwords must match')
      .required('Confirm password is required')
  });

  // Formik setup
  const formik = useFormik({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setChangePasswordError(null);
      setChangePasswordSuccess(false);
      
      try {
        await changePassword(values.currentPassword, values.newPassword);
        setChangePasswordSuccess(true);
        formik.resetForm();
      } catch (err: any) {
        setChangePasswordError(err.message || 'Failed to change password. Please try again.');
      }
    }
  });

  return (
    <Box sx={{ 
      flexGrow: 1,
      minHeight: '100vh',
      backgroundColor: 'var(--joan-bg)',
      backgroundImage: `linear-gradient(to right, ${BRAND.veilStrong} ${GOLDEN_MINOR}, ${BRAND.veilSoft} 100%), url(${landingBackground})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: { md: 'fixed' },
    }}>
      <SiteHeader
        actions={<HeaderButton to="/profile" icon={<PersonOutlineIcon />} iconOnlyOnPhone>My Profile</HeaderButton>}
      />

      <Container component="main" maxWidth="sm" sx={{ py: { xs: 4, md: 5 }, px: { xs: 2, sm: 3 } }}>
        {/* Breadcrumbs */}
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3, color: BRAND.muted, '& .MuiTypography-root': { color: BRAND.ink } }}>
          <Link component={RouterLink} to="/" color="inherit">
            Home
          </Link>
          <Link component={RouterLink} to="/profile" color="inherit">
            Profile
          </Link>
          <Typography color="text.primary">Change Password</Typography>
        </Breadcrumbs>

        <Paper sx={{ 
          p: { xs: 3, sm: 4 },
          borderRadius: '13px',
          ...frostedSx,
          backgroundColor: BRAND.card,
          border: `1px solid ${BRAND.line}`,
          boxShadow: BRAND.shadow,
        }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Change Password
          </Typography>
          
          {changePasswordSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Password changed successfully!
            </Alert>
          )}
          
          {(changePasswordError || error) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {changePasswordError || error}
            </Alert>
          )}

          <form onSubmit={formik.handleSubmit}>
            <TextField
              margin="normal"
              required
              fullWidth
              name="currentPassword"
              label="Current Password"
              type="password"
              id="currentPassword"
              autoComplete="current-password"
              value={formik.values.currentPassword}
              onChange={formik.handleChange}
              error={formik.touched.currentPassword && Boolean(formik.errors.currentPassword)}
              helperText={formik.touched.currentPassword && formik.errors.currentPassword}
              disabled={isLoading}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              name="newPassword"
              label="New Password"
              type="password"
              id="newPassword"
              autoComplete="new-password"
              value={formik.values.newPassword}
              onChange={formik.handleChange}
              error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
              helperText={formik.touched.newPassword && formik.errors.newPassword}
              disabled={isLoading}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm New Password"
              type="password"
              id="confirmPassword"
              autoComplete="new-password"
              value={formik.values.confirmPassword}
              onChange={formik.handleChange}
              error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
              helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
              disabled={isLoading}
            />
            
            <Box sx={{ mt: 4, display: 'flex', flexDirection: { xs: 'column-reverse', sm: 'row' }, justifyContent: 'space-between', gap: 2 }}>
              <Button
                component={RouterLink}
                to="/profile"
                variant="outlined"
                disabled={isLoading}
              >
                Back to Profile
              </Button>
              
              <Button
                type="submit"
                variant="contained"
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Change Password'}
              </Button>
            </Box>
          </form>
        </Paper>
      </Container>
      <SiteFooter />
    </Box>
  );
};

export default PasswordChange;
